import { ChangeEvent } from 'react';
import { SlidersVertical } from '@sweep-io/sweep-design/dist/icons';
import { SingleSelectFilter } from '../../common/filters/single-select-filter/SingleSelectFilter';
import { SingleSelectFilterPopoverOption } from '../../common/filters/single-select-filter/SingleSelectFilterPopover';
import { OPTION_ALL } from '../../common/filters/single-select-filter/utils';
import { ConfigurationType } from '../dependencies/types';
import Box from '@mui/material/Box/Box';
import { globalSearchSx } from '../../pages/canvas-pages/canvasPanelConsts';
import SearchInputWithTooltip from '../../common/SearchInputWithTooltip';
import { useUniversalSearchContext } from './UniversalSearchContext';
import { Tag } from '@sweep-io/sweep-design';
import { SearchType } from './types';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';

interface UniversalSearchInputProps {
  searchTxt: string;
  tempSearchTxt: string;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onClearButtonClick: () => void;
  filterOptions: SingleSelectFilterPopoverOption[];
  onSelectedFilterItem: (key: ConfigurationType) => void;
  selectedFilterValue?: string;
  isUniversalSearchListOpen?: boolean;
  menuOptions?: SingleSelectFilterPopoverOption[];
  selectedMenuOption?: string;
  onSelectMenuOption?: (selectedValue: string) => void;
  isExpandedMode?: boolean;
  searchInputSize?: 'small' | 'default';
  autoFocus?: boolean;
}

export const UniversalSearchInput = ({
  onKeyDown,
  searchTxt,
  tempSearchTxt,
  onChange,
  onClearButtonClick,
  filterOptions,
  menuOptions,
  selectedMenuOption,
  onSelectMenuOption,
  selectedFilterValue = OPTION_ALL.value,
  onSelectedFilterItem,
  isUniversalSearchListOpen,
  isExpandedMode,
  searchInputSize = 'small',
  autoFocus,
}: UniversalSearchInputProps) => {
  const { universalSearchRedesign } = useFeatureToggle();
  const { setIsUniversalSearchListOpen } = useUniversalSearchContext();
  const displayTooltip = !!tempSearchTxt && tempSearchTxt !== searchTxt;
  const isContentSearch = selectedMenuOption === SearchType.content;

  return (
    <Box
      sx={{
        ...globalSearchSx(!!isExpandedMode),
        borderRadius: 0,
        width: '100%',
      }}
    >
      <Box maxWidth={isExpandedMode ? '348px' : 'auto'}>
        <SearchInputWithTooltip
          displayTooltip={displayTooltip}
          onClearButtonClick={onClearButtonClick}
          size={searchInputSize}
          endAdornment={
            <>
              {universalSearchRedesign && isContentSearch && onSelectMenuOption && (
                <Tag
                  label="By metadata"
                  onDelete={() => onSelectMenuOption(SearchType.apiNameAndLabel)}
                  size="tiny"
                />
              )}

              {isUniversalSearchListOpen && (
                <SingleSelectFilter
                  options={filterOptions}
                  selectedItemValue={selectedFilterValue}
                  onSelectItem={(key) => onSelectedFilterItem(key as ConfigurationType)}
                  icon={<SlidersVertical size="medium" />}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  menuOptions={menuOptions}
                  selectedMenuOption={selectedMenuOption}
                  onSelectMenuOption={onSelectMenuOption}
                />
              )}
            </>
          }
          withFixedMagnifyingGlassIcon
          TextFieldProps={{
            onChange,
            value: tempSearchTxt,
            placeholder: 'Search configurations',
            onKeyDown,
            onFocus: () => setIsUniversalSearchListOpen(true),
            autoFocus: true,
            sx: {
              '& .MuiOutlinedInput-root input': {
                paddingRight: '4px',
              },
            },
          }}
          forceCloseButton={isUniversalSearchListOpen && searchTxt !== ''}
          autoFocus={autoFocus}
        />
      </Box>
    </Box>
  );
};
